import { ref, nextTick } from 'vue';
import type { OfferDto, OfferImage } from '../types/offer.types';

interface ImageUploaderInstance {
  uploadAll: (offerId?: number) => Promise<void>;
  items: any;
  reset: () => void;
  setExistingImages: (images: NormalizedImage[]) => void;
  saveOrder: (offerId?: number) => Promise<void>;
}

interface NormalizedImage {
  id?: string;
  filename?: string;
  url: string;
  is_cover?: number;
}

export function useImageUploader() {
  const uploaderRef = ref<ImageUploaderInstance | null>(null);

  function normalizeImages(rawImages: OfferImage[]): NormalizedImage[] {
    return rawImages
      .map((image) => {
        if (!image) return null;
        const url = image.url || null;
        return url ? {
          id: image.id,
          filename: image.filename,
          url,
          is_cover: image.is_cover ?? 0
        } : null;
      })
      .filter((image): image is NormalizedImage => !!image && !!image.url);
  }

  async function applyOfferImages(offer: OfferDto | null) {
    if (!uploaderRef.value || !offer) return;

    uploaderRef.value.reset();
    const rawImages = offer.images ?? [];
    
    console.debug('[useImageUploader] offer.images raw:', rawImages);
    
    const normalizedImages = normalizeImages(rawImages);
    console.debug('[useImageUploader] normalized images:', normalizedImages);
    
    if (normalizedImages.length) {
      uploaderRef.value.setExistingImages(normalizedImages);
    }
  }

  async function saveImageOrder(offerId: number): Promise<void> {
    if (uploaderRef.value?.saveOrder) {
      return uploaderRef.value.saveOrder(offerId);
    }
  }

  async function uploadAllImages(offerId: number): Promise<void> {
    if (uploaderRef.value?.uploadAll) {
      return uploaderRef.value.uploadAll(offerId);
    }
  }

  function waitForUploaderAndApply(offer: OfferDto | null) {
    nextTick(() => {
      if (uploaderRef.value) {
        applyOfferImages(offer);
      }
    });
  }

  return {
    uploaderRef,
    applyOfferImages,
    saveImageOrder,
    uploadAllImages,
    waitForUploaderAndApply,
  };
}